import { HTMLProps, RefObject, useEffect, useRef, useState } from 'react'
import { Modal, Placeholder } from '@telegram-apps/telegram-ui'
import cn from 'classnames'

import { ClockIcon, Input } from 'components'

import './styles.scss'

export interface Props extends Omit<HTMLProps<HTMLInputElement>, 'onChange'> {
  value: string
  onChange: (value: string) => void
  onModalOpened?: (state: boolean) => void
  scrollToOnFocus?: boolean
  selectOnFocus?: boolean
  selectOnRender?: boolean
}

const scrollToActive = (containerRef: RefObject<HTMLDivElement>) => {
  const container = containerRef.current
  if (!container) return

  const activeElement = container.querySelector('.select-value.active')
  if (!activeElement) return

  // Центрируем элемент
  const containerHeight = container.clientHeight
  const elementTop = (activeElement as HTMLElement).offsetTop

  container.scrollTop = elementTop - containerHeight / 2
}

const getTimeValues = (length: number) => [
  '00',
  ...Array.from({ length }, (_, i) => (i + 1).toString().padStart(2, '0')),
]
const hours = getTimeValues(23)
const minutes = getTimeValues(59)

export const TimePicker = ({ value, className, onChange, onModalOpened, ...rest }: Props) => {
  const [open, setOpen] = useState(false)
  const hoursRef = useRef<HTMLDivElement>(null)
  const minutesRef = useRef<HTMLDivElement>(null)

  const [hh, mm] = (value || '').split(':')

  const onChangeHours = (hour: string) => onChange(`${hour}:${mm}`)
  const onChangeMinutes = (minutes: string) => onChange(`${hh}:${minutes}`)
  const onOpenModal = (state: boolean) => {
    setOpen(state)
    onModalOpened?.(state)
  }

  useEffect(() => {
    if (!open) return

    const observer = new MutationObserver(() => {
      if (hoursRef.current && minutesRef.current) {
        scrollToActive(hoursRef)
        scrollToActive(minutesRef)
        observer.disconnect()
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [open])

  return (
    <div className="time-picker">
      <Modal className="picker-modal" open={open} onOpenChange={onOpenModal} dismissible>
        <Placeholder>
          <div className="time-picker-selects">
            <div className="time-picker-select" ref={hoursRef}>
              {hours.map((hour) => (
                <div
                  key={hour}
                  className={cn('select-value', { active: hour === hh })}
                  onClick={() => onChangeHours(hour)}
                >
                  {hour}
                </div>
              ))}
            </div>
            <div className="time-picker-select" ref={minutesRef}>
              {minutes.map((minute) => (
                <div
                  key={minute}
                  className={cn('select-value', { active: minute === mm })}
                  onClick={() => onChangeMinutes(minute)}
                >
                  {minute}
                </div>
              ))}
            </div>
          </div>
        </Placeholder>
      </Modal>
      <Input
        mask="99:99"
        value={value}
        onChange={onChange}
        className={cn('form-input time-picker-input', className)}
        inputMode="numeric"
        {...rest}
      />
      <ClockIcon
        className="time-picker-icon"
        width={16}
        height={16}
        onClick={() => setOpen(true)}
      />
    </div>
  )
}
